import React, { MouseEventHandler, ReactNode, useEffect } from "react";
import dompurify from "dompurify";
import { Viewer } from "@toast-ui/react-editor";
import moment from "moment";
import IconButton from "../common/IconButton";
import SVG from "src/utils/SVG";
import styled from "styled-components";
import { category } from "../../constants/notice";
import iconNotiPin from "src/assets/images/iconNotiPin.svg";

interface NoticeDetailInfoProps {
  noticeData: ModifyNotice;
}

const NoticeDetailInfo = (props: NoticeDetailInfoProps) => {
  const imageServerIp = process.env.REACT_APP_IMAGE_SERVER_IP;
  function getCategoryName(value: string) {
    let result = "";
    const getData = category.filter((cat) => cat.value == value);
    if (getData.length > 0) {
      result = getData[0].name;
    }
    return result;
  }
  async function download(file: Attachments) {
    try {
      const getData = await fetch(imageServerIp + file.file_url, { method: "GET" });
      const blobData = await getData.blob();
      const url = window.URL.createObjectURL(blobData);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.file_origin_name;
      document.body.appendChild(a);
      setTimeout((_: any) => {
        window.URL.revokeObjectURL(url);
      }, 60000);
      a.remove();

      // fileReader.readAsDataURL(blobData);
    } catch (e) {
      console.error(e);
    }
  }
  function openPreview(uri: string) {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ actionType: "MODAL", uri }));
    } else {
      window.open(uri, "_blank");
    }
  }

  return (
    <DetailInfoBox>
      <p className="category">{getCategoryName(props.noticeData.cate_idx)}</p>
      <p className="title">
        {props.noticeData.title}
        {props.noticeData.important_yn == "Y" && <img src={iconNotiPin} />}
      </p>
      <Viewer initialValue={dompurify.sanitize(props.noticeData.contents)} />
      <div className="file-list">
        {props.noticeData.attachments.length > 0 && <p>첨부파일</p>}
        <ul>
          {props.noticeData.attachments.map((file, index) => (
            <li key={index}>
              <p
                className={"file-name"}
                onClick={() => {
                  openPreview(process.env.REACT_APP_PREVIEW + file.file_url);
                }}
              >
                {file.file_origin_name}
              </p>
              <p className="file-size">{(file.size / 1000 ** 2).toFixed(2) + "MB"}</p>
              <a href={imageServerIp + file.file_url} download={file.file_origin_name}>
                <IconButton btnName="첨부파일 삭제하기">
                  <SVG name="fileDownload" width={24} height={24} viewBox="0 0 24 24" />
                </IconButton>
              </a>
            </li>
          ))}
        </ul>
      </div>
      <p>
        <span className="name">{props.noticeData.vmd_name}</span>
        <span className="date">{moment(props.noticeData.insert_date).format("YYYY.MM.DD HH:mm")}</span>
        {props.noticeData.end_date && <span className="end-date">{moment(props.noticeData.end_date).format("YYYY.MM.DD")} 마감</span>}
      </p>
    </DetailInfoBox>
  );
};

const DetailInfoBox = styled.div`
  padding: 20px 15px 40px;
  .category {
    margin-bottom: 8px;
    color: ${({ theme }) => theme.color.tertiary};
    font-size: 12px;
  }
  .title {
    margin-bottom: 16px;
    color: #000;
    font-size: 18px;
    font-weight: 700;
    line-height: 22px;
    word-break: break-all;
    > img {
      margin-left: 4px;
    }
  }
  .content {
    margin-bottom: 40px;
    word-break: break-word;
    white-space: pre-wrap;
    ${/* 에디터 뷰영역 리셋 초기화 */ ""}
    .h1 {
      font-size: 24px !important;
    }
    .h2 {
      font-size: 22px;
    }
    .h3 {
      font-size: 20px;
    }
    .h4 {
      font-size: 18px;
    }
    .h5 {
      font-size: 16px;
    }
    .h6 {
      font-size: 14px;
    }
    strong {
      font-weight: bold;
    }
    em {
      font-style: italic;
    }
  }
  .file-list {
    > p {
      display: inline-block;
      height: 20px;
      padding: 0 6px;
      border: 1px solid #000;
      border-radius: 10px;
      font-size: 12px;
      line-height: 18px;
      text-align: center;
    }
    > ul {
      padding: 8px 0 24px;
      > li {
        ${({ theme }) => theme.flexBox.flexMixin()}
        padding: 6px;
        margin-top: 8px;
        border: 1px solid ${({ theme }) => theme.color.linePrimary};
        color: ${({ theme }) => theme.color.tertiary};
        &:first-child {
          margin-top: 0;
        }
        .file-name {
          flex-grow: 1;
          ${({ theme }) => theme.textOverflow.singleLine()}
        }
        .file-size {
          position: relative;
          flex-grow: 0;
          padding-left: 16px;
          margin-left: 16px;
          color: ${({ theme }) => theme.color.quaternary};
          &:before {
            position: absolute;
            left: 0;
            content: "";
            display: block;
            width: 2px;
            height: 16px;
            background-color: ${({ theme }) => theme.color.lineSecondary};
          }
        }
        button {
          flex-grow: 0;
          margin-left: 16px;
        }
      }
    }
  }
  .name {
    padding-right: 16px;
    color: ${({ theme }) => theme.color.secondary};
  }
  .date {
    color: ${({ theme }) => theme.color.tertiary};
  }
  .end-date {
    padding-left: 8px;
    margin: 8px 0 0 8px;
    border-left: 2px solid ${({ theme }) => theme.color.lineSecondary};
    color: ${({ theme }) => theme.color.warning};
    font-weight: 500;
  }
`;
export default NoticeDetailInfo;
